import { useMemo, useState } from "react";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useVendors } from "./vendors.hooks";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";
import { Vendor, VendorService, VendorSortKey } from "@/repositories/vendors.repository";
import { InviteVendorModal } from "./components/invite.modal";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { getInitials } from "@/lib/utils.ts";

export default function VendorsPage() {
  const {
    vendors,
    total,
    page,
    pageSize,
    name,
    service,
    setPage,
    setPageSize,
    setName,
    setService,
    sortKey,
    sortOrder,
    setSortKey,
    removeVendor,
    inviteOpen,
    inviteSaving,
    inviteError,
    openInviteModal,
    sendInvite,
  } = useVendors();
  const administersCustomer = useAdministersActiveOrganization();

  const [removeId, setRemoveId] = useState<string | null>(null);

  const formatServicesDisplay = (services: VendorService[], maxVisible = 3) => {
    if (services.length === 0) return "—";
    if (services.length <= maxVisible) {
      return services.map((s) => s.name).join(", ");
    }
    const visibleServices = services.slice(0, maxVisible);
    const remainingCount = services.length - maxVisible;
    return `${visibleServices.map((s) => s.name).join(", ")}, +${remainingCount} more`;
  };

  const serviceOptions = useMemo(() => {
    const names = Array.from(new Set(vendors.flatMap((v) => v.services.map((s) => s.name))));
    return names.map((n) => ({ label: n, value: n }));
  }, [vendors]);

  const columns: ColumnDef<Vendor>[] = [
    {
      header: "Name",
      accessor: (row) => (
        <Link to={`/vendors/${row.id}`} className="hover:underline flex items-center">
          <Avatar className="w-8 h-8 mr-4">
            <AvatarImage src={row.image ?? undefined} />
            <AvatarFallback>{getInitials(row.name)}</AvatarFallback>
          </Avatar>
          {row.name}
        </Link>
      ),
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Email",
      accessor: (row) => row.email,
      sortable: true,
      sortKey: "email",
    },
    {
      header: "Services",
      accessor: (row) => formatServicesDisplay(row.services),
    },
    {
      header: "Actions",
      accessor: (row) => (
        <div className="flex justify-end gap-2">
          <Link to={`/vendors/${row.id}`}>
            <Button variant="outline" size="sm">
              View
            </Button>
          </Link>

          {administersCustomer && (
            <Dialog open={removeId === row.id} onOpenChange={(open) => setRemoveId(open ? row.id : null)}>
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  Remove
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Remove Vendor?</DialogTitle>
                </DialogHeader>
                <p className="pt-2 text-sm text-muted-foreground">
                  Are you sure you want to remove <strong>{row.name}</strong>? You can re-invite them
                  later if needed.
                </p>
                <DialogFooter className="pt-4">
                  <Button variant="outline" onClick={() => setRemoveId(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      setRemoveId(null);
                      try {
                        await removeVendor(row.id);
                        toast.success("Vendor removed successfully.");
                      } catch (error) {
                        toast.error((error as { message?: string }).message ?? "Failed to remove the vendor.");
                      }
                    }}
                  >
                    Remove
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      ),
      className: "whitespace-nowrap",
    },
  ];

  const filterItems: FilterItem[] = [
    {
      type: "search",
      title: "Vendor Name",
      value: name,
      onValueChange: setName,
    },
    {
      type: "select",
      title: "Service",
      value: service,
      onValueChange: setService,
      options: serviceOptions,
    },
  ];

  return (
    <MainLayout
      breadcrumb={[{ name: "Vendors", href: "#" }]}
      pageHeader={{
        title: "Vendors",
        subtitle: "Manage service vendors",
        buttonText: "+ Invite New Vendor",
        onButtonClick: () => openInviteModal(true),
      }}
    >
      <SimpleTable<Vendor>
        columns={columns}
        data={vendors}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSortChange={(key) => setSortKey(key as VendorSortKey)}
        filters={filterItems}
        onReset={() => {
          setName("");
          setService("");
        }}
        onApply={() => {}}
      />

      <InviteVendorModal
        open={inviteOpen}
        saving={inviteSaving}
        error={inviteError}
        onOpenChange={openInviteModal}
        onSubmit={sendInvite}
      />
    </MainLayout>
  );
}
